import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getRecall, generateRecallImpact, setAffectedStatus, closeRecall } from "@/lib/recalls.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/compliance/recalls/$id")({ component: RecallDetail });

function RecallDetail() {
  const { id } = Route.useParams();
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const getFn = useServerFn(getRecall);
  const impactFn = useServerFn(generateRecallImpact);
  const statusFn = useServerFn(setAffectedStatus);
  const closeFn = useServerFn(closeRecall);

  const { data, isLoading } = useQuery({
    queryKey: ["recall", tenantId, id],
    enabled: !!tenantId,
    queryFn: () => getFn({ data: { tenantId: tenantId!, id } }),
  });

  const impact = useMutation({
    mutationFn: () => impactFn({ data: { tenantId: tenantId!, recallId: id } }),
    onSuccess: (r) => { toast.success(`Added ${r.inserted} affected; placed ${r.holds_placed} order holds`); qc.invalidateQueries({ queryKey: ["recall", tenantId, id] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: "pending"|"notified"|"returned"|"destroyed" }) =>
      statusFn({ data: { tenantId: tenantId!, id: v.id, status: v.status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recall", tenantId, id] }),
    onError: (e: any) => toast.error(e.message),
  });
  const close = useMutation({
    mutationFn: () => closeFn({ data: { tenantId: tenantId!, id } }),
    onSuccess: () => { toast.success("Recall closed"); qc.invalidateQueries({ queryKey: ["recall", tenantId, id] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const exportCsv = () => {
    const rows = data?.affected ?? [];
    const header = ["order_id","shipment_id","customer_contact","qty","status","notified_at","resolved_at"];
    const csv = [header.join(","), ...rows.map((r: any) => header.map((h) => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `recall-${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading || !data) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const r = data.recall;

  return (
    <>
      <PageHeader title={`Recall — ${r.severity}`}
        description={r.reason}
        meta={<Link to="/app/compliance/recalls" className="text-xs text-primary underline">← Back</Link>}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCsv}>Export CSV</Button>
            <Button onClick={() => impact.mutate()} disabled={impact.isPending}>Recalculate impact</Button>
            {r.status !== "closed" && <Button variant="destructive" onClick={() => close.mutate()} disabled={close.isPending}>Close</Button>}
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
          <div><div className="text-xs text-muted-foreground">Status</div><Badge>{r.status}</Badge></div>
          <div><div className="text-xs text-muted-foreground">Lot</div><div className="font-mono">{r.lot_code ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Regulator ref</div><div>{r.regulator_ref ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Initiated</div><div>{new Date(r.initiated_at).toLocaleString()}</div></div>
        </div>
        <div className="border rounded-md bg-card overflow-x-auto">
          <div className="p-3 border-b text-sm font-medium">Affected ({data.affected.length})</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Order</TableHead><TableHead>Shipment</TableHead><TableHead>Contact</TableHead>
              <TableHead className="text-right">Qty</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right w-72">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.affected.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm">No affected records yet — click “Recalculate impact”.</TableCell></TableRow>}
              {data.affected.map((a: any) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono text-xs">{a.order_id?.slice(0, 8)}</TableCell>
                  <TableCell className="font-mono text-xs">{a.shipment_id?.slice(0, 8) ?? "—"}</TableCell>
                  <TableCell className="text-xs">{a.customer_contact ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{a.qty}</TableCell>
                  <TableCell><Badge variant={a.status === "pending" ? "secondary" : "default"}>{a.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {a.status === "pending" && <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: a.id, status: "notified" })}>Mark notified</Button>}
                    {a.status !== "returned" && a.status !== "destroyed" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: a.id, status: "returned" })}>Returned</Button>
                        <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: a.id, status: "destroyed" })}>Destroyed</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}